import {
  IEmptyValidatable,
  instanceOfIEmptyValidatable,
  INameGroupEmptyValidatable,
  instanceOfINameGroupEmptyValidatable,
  RelationalActionParameter,
  IItem,
  instanceOfIEmptyValidationSuppressible,
  instanceOfIHisInformationApplicable,
  instanceOfIRuleInjectable,
  instanceOfISubNameSpecifiable,
} from "../items";
import { IDocumentAlert } from "../IDocumentAlert";
import { SingleEmptyAlert, GroupEmptyAlert, NameGroupEmptyAlert } from "../alerts";
import { AbstractItemsValidator } from "./AbstractItemsValidator";
// index.ts経由にするとimportの無限ループに陥ったので暫定で直接指定にする.
import { HisInformationNameEmptyValidationMaster } from "../../../domain/his/master/HisInformationNameEmptyValidationMaster";

/**
 * 制約の注入での名称警告ではサブ名称も見る必要がある
 * いったん判定に必要な情報を抽出したのちまとめて処理している
 */
class NameGroupDetailItem {
  public name: string;
  public subName: string;
  public item: IItem & INameGroupEmptyValidatable;
  public isEmpty: boolean;

  public isKeyEqual(target: NameGroupDetailItem): boolean {
    return !!target && (target.name === this.name) && (target.subName === this.subName);
  }
}

export class EmptyValidator extends AbstractItemsValidator {
  protected validateItems(items: IItem[], itemSetId: number): IDocumentAlert[] {
    const alerts: IDocumentAlert[] = [];
    const valueContainedGroupKeys: string[] = [];
    const allGroupItems: { [key: string]: IItem & IEmptyValidatable } = {};
    const valueContainedNameGroupKeys: string[] = [];
    const allNameGroupItems: { [key: string]: IItem & INameGroupEmptyValidatable } = {};
    const ruleInjectedNameGroupDetailItems: NameGroupDetailItem[] = [];

    const suppressTargets = this.getSuppressTargetsByParentAlert(items);

    for (const item of items) {
      if (!item) { continue; }
      if (this.isSuppressTarget(suppressTargets, item)) { continue; }
      if (instanceOfIEmptyValidatable(item)) {
        // 単体警告
        if (item.enableSingleEmptyCheck && item.isEmpty) {
          alerts.push(new SingleEmptyAlert(itemSetId, item));
        }
        // グループ警告
        if (item.alertGroupKey) {
          // TODO:V1の仕様確認
          if (!allGroupItems[item.alertGroupKey]) {
            allGroupItems[item.alertGroupKey] = item;
          }
          if (!item.isEmpty) {
            if (valueContainedGroupKeys.indexOf(item.alertGroupKey) < 0) {
              valueContainedGroupKeys.push(item.alertGroupKey);
            }
          }
        }
      }
      // 名称警告
      if (instanceOfINameGroupEmptyValidatable(item)) {
        // TODO:V1の仕様確認
        if (item.enableNameGroupEmptyCheck 
          && !allNameGroupItems[item.name]
          && this.shouldValidateItem(item)) {
          allNameGroupItems[item.name] = item;
        }
        if (!item.isEmpty) {
          if (valueContainedNameGroupKeys.indexOf(item.name) < 0) {
            valueContainedNameGroupKeys.push(item.name);
          }
        }
        if (this.isNameGroupEmptyCheckEnabledByInjectedRule(item) && this.shouldValidateItem(item)) {
          const detail = this.createNameGroupDetailItem(item);
          ruleInjectedNameGroupDetailItems.push(detail);
        }
      }
    }
    for (const alertGroupKey in allGroupItems) {
      if (valueContainedGroupKeys.indexOf(alertGroupKey) < 0) {
        alerts.push(new GroupEmptyAlert(itemSetId, allGroupItems[alertGroupKey]));
      }
    }
    for (const groupKey in allNameGroupItems) {
      if (valueContainedNameGroupKeys.indexOf(groupKey) < 0) {
        alerts.push(new NameGroupEmptyAlert(itemSetId, allNameGroupItems[groupKey]));
      }
    }
    const emptyDetailItems = this.extractDistinctEmptyNameGroupDetailItem(ruleInjectedNameGroupDetailItems);
    for (const emptyDetailItem of emptyDetailItems) {
      alerts.push(new NameGroupEmptyAlert(itemSetId, emptyDetailItem.item));
    }
    return alerts;
  }

  private createNameGroupDetailItem(item: IItem & INameGroupEmptyValidatable): NameGroupDetailItem {
    const detailItem = new NameGroupDetailItem();
    detailItem.name = item.name;
    detailItem.subName = (instanceOfISubNameSpecifiable(item) && !!item.subName) ? item.subName : null;
    detailItem.item = item;
    detailItem.isEmpty = item.isEmpty;
    return detailItem;
  }

  private extractDistinctEmptyNameGroupDetailItem(details: NameGroupDetailItem[]): NameGroupDetailItem[] {
    const extractedEmptyDetailItems: NameGroupDetailItem[] = [];
    for (const emptyDetailItem of details.filter(d => d.isEmpty)) {
      if (extractedEmptyDetailItems.some(d => emptyDetailItem.isKeyEqual(d))) {
        // すでに同一キーで抽出されている
        continue;
      }
      if (details.some(d => !d.isEmpty && emptyDetailItem.isKeyEqual(d))) {
        // 同一キーの他オブジェクトに値が入っている
        continue;
      }
      extractedEmptyDetailItems.push(emptyDetailItem);
    }
    return extractedEmptyDetailItems;
  }

  private isNameGroupEmptyCheckEnabledByInjectedRule(item: IItem): boolean {
    return instanceOfIRuleInjectable(item) && item.injectedRule && item.injectedRule.isNameGroupEmptyCheckEnabled;
  }

  private getSuppressTargetsByParentAlert(items: IItem[]): RelationalActionParameter[] {
    const suppressTargets: RelationalActionParameter[] = [];
    for (const item of items) {
      if (!instanceOfIEmptyValidationSuppressible(item)) { continue; }
      if (!item.isSuppressionActive) { continue; }
      Array.prototype.push.apply(suppressTargets, item.parentAlertInformations);
    }
    return suppressTargets;
  }

  private isSuppressTarget(suppressTargets: RelationalActionParameter[], item: any): boolean {
    if (!item) { return false; }
    // TODO:V1の仕様の確認
    // TODO:pageKeyの対応
    for (const suppressTarget of suppressTargets) {
      if (suppressTarget.itemType === "1") {
        // オブジェクト指定で警告を無効にする
        if (suppressTarget.objectId === item.id) {
          return true;
        }
      }
      if (suppressTarget.itemType === "2") {
        // 名称指定で警告を無効にする
        if (suppressTarget.value === item.name) {
          return true;
        }
      }
    }
    return false;
  }

  private shouldValidateItem(item: IItem): boolean {
    return !instanceOfIHisInformationApplicable(item)
      || HisInformationNameEmptyValidationMaster.shouldValidateNameEmpty(item.hisItemParameter);
  }
}
